import { useVirtualizer } from '@tanstack/react-virtual';
import { type KeyboardEvent, memo, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { Spinner } from '@air/primitive-spinner';

import {
  WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE,
  WORKSPACE_AGENT_MENTION_ROW_GAP_PX,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionRowChrome';
import {
  toWorkspaceAgentSelectAssetVirtualRows,
  WORKSPACE_AGENT_SELECT_ASSET_LIBRARIES_HEADING_ROW_ESTIMATE_PX,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/toWorkspaceAgentSelectAssetVirtualRows';
import { WorkspaceAgentSelectAssetVirtualRowView } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetVirtualRowView';
import { type WorkspaceAgentSelectAssetResults } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { toWorkspaceAgentMentionSentinelEndIndexes } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

const WORKSPACE_AGENT_SELECT_ASSET_ROW_ESTIMATE_PX = 36;

const WORKSPACE_AGENT_SELECT_ASSET_LIST_OVERSCAN = 8;

export type WorkspaceAgentSelectAssetVirtualListProps = {
  activeOptionId?: string;
  ariaLabel: string;
  emptyStateTitle: string;
  highlightedIndex: number;
  isKeyboardHighlight: boolean;
  /** Resets flat-list pagination when the underlying search/board context changes. */
  listPaginationKey: string;
  listRef: RefObject<HTMLDivElement>;
  onHighlight: (index: number) => void;
  /**
   * Scroll the highlight to the top of the list without the keyboard ring. The choose-board menu
   * uses this so the current board starts at the top even when it sits below the fold. Keyboard
   * moves keep `align: 'auto'`.
   */
  scrollHighlightedIntoView?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  onSelectItem: (itemId: string) => void;
  results: WorkspaceAgentSelectAssetResults;
  role: 'listbox' | 'tree';
};

export const WorkspaceAgentSelectAssetVirtualList = memo(
  ({
    activeOptionId,
    ariaLabel,
    emptyStateTitle,
    highlightedIndex,
    isKeyboardHighlight,
    listPaginationKey,
    listRef,
    onHighlight,
    onKeyDown,
    onSelectItem,
    scrollHighlightedIntoView = false,
    results,
    role,
  }: WorkspaceAgentSelectAssetVirtualListProps) => {
    const {
      boardListQueries,
      hasNextPage,
      hasNextPageNodeIds,
      isError,
      isFetching,
      isLoading,
      isStale,
      items,
      onFetchNextPage,
      getIsBoardItemDisabled,
      onFetchNextPageForNode,
      onToggleExpanded,
      pendingChildCountNodeIds,
    } = results;
    const hasRows = items.length > 0;
    const hasFailed = isError && !hasRows;
    const isResolving = !hasRows && !hasFailed && (isLoading || isFetching || isStale);
    const clampedHighlightedIndex = hasRows ? Math.min(highlightedIndex, items.length - 1) : 0;
    const virtualRows = useMemo(() => toWorkspaceAgentSelectAssetVirtualRows(items), [items]);
    const sentinelEndIndexes = useMemo(
      () => toWorkspaceAgentMentionSentinelEndIndexes([...items], hasNextPageNodeIds),
      [hasNextPageNodeIds, items],
    );

    const getScrollElement = useCallback(() => listRef.current, [listRef]);

    const estimateSize = useCallback(
      (index: number) => {
        const row = virtualRows[index];

        if (row?.kind === 'libraries-heading') {
          return WORKSPACE_AGENT_SELECT_ASSET_LIBRARIES_HEADING_ROW_ESTIMATE_PX;
        }

        return WORKSPACE_AGENT_SELECT_ASSET_ROW_ESTIMATE_PX;
      },
      [virtualRows],
    );

    const getItemKey = useCallback((index: number) => virtualRows[index]?.id ?? index, [virtualRows]);
    // A last row cannot sit at the top unless the list has a viewport of space after it.
    // The scroller is a column flex container, so that space has to live on a shrink-0 box —
    // flex shrink would collapse it and the browser would clamp the row to the bottom.
    const [scrollPinPadding, setScrollPinPadding] = useState(0);
    const pinnedRowIdRef = useRef<string | null>(null);
    const scrollerClientHeight = listRef.current?.clientHeight ?? 0;

    const virtualizer = useVirtualizer({
      count: virtualRows.length,
      estimateSize,
      gap: WORKSPACE_AGENT_MENTION_ROW_GAP_PX,
      getItemKey,
      getScrollElement,
      overscan: WORKSPACE_AGENT_SELECT_ASSET_LIST_OVERSCAN,
      paddingEnd: scrollPinPadding,
    });

    useEffect(() => {
      if (!scrollHighlightedIntoView) {
        pinnedRowIdRef.current = null;
        setScrollPinPadding(0);
      }
    }, [scrollHighlightedIntoView]);

    useIsomorphicLayoutEffect(() => {
      if ((!isKeyboardHighlight && !scrollHighlightedIntoView) || !hasRows) {
        return;
      }

      const virtualRowIndex = virtualRows.findIndex(
        (row) => row.kind === 'item' && row.itemIndex === clampedHighlightedIndex,
      );

      if (virtualRowIndex < 0) {
        return;
      }

      if (!isKeyboardHighlight) {
        const rowId = virtualRows[virtualRowIndex]?.id;

        if (rowId !== undefined && pinnedRowIdRef.current === rowId) {
          return;
        }

        if (scrollerClientHeight > 0 && scrollPinPadding < scrollerClientHeight) {
          setScrollPinPadding(scrollerClientHeight);
          return;
        }

        const pinnedOffset = virtualizer.getOffsetForIndex(virtualRowIndex, 'start')?.[0];
        const scroller = listRef.current;

        if (scroller && pinnedOffset !== undefined) {
          scroller.scrollTop = pinnedOffset;
          pinnedRowIdRef.current = rowId ?? null;
          return;
        }

        virtualizer.scrollToIndex(virtualRowIndex, { align: 'start' });
        pinnedRowIdRef.current = rowId ?? null;
        return;
      }

      virtualizer.scrollToIndex(virtualRowIndex, { align: 'auto' });
    }, [
      clampedHighlightedIndex,
      hasRows,
      isKeyboardHighlight,
      listRef,
      scrollHighlightedIntoView,
      scrollPinPadding,
      scrollerClientHeight,
      virtualRows,
      virtualizer,
    ]);

    const virtualItems = virtualizer.getVirtualItems();
    const lastVisibleRowIndex = virtualItems[virtualItems.length - 1]?.index;
    const fetchedListLengthRef = useRef<number | null>(null);
    const fetchedNodeEndIndexesRef = useRef(new Map<string, number>());
    const listPaginationKeyRef = useRef(listPaginationKey);

    useEffect(() => {
      if (listPaginationKeyRef.current !== listPaginationKey) {
        listPaginationKeyRef.current = listPaginationKey;
        fetchedListLengthRef.current = null;
      }

      const isAtListEnd =
        hasNextPage &&
        !isFetching &&
        !isStale &&
        lastVisibleRowIndex !== undefined &&
        lastVisibleRowIndex >= virtualRows.length - 1;

      if (isAtListEnd && fetchedListLengthRef.current !== virtualRows.length) {
        fetchedListLengthRef.current = virtualRows.length;
        onFetchNextPage();
      }

      const visibleNodeIds = new Set<string>();

      if (!isStale) {
        for (const virtualItem of virtualizer.getVirtualItems()) {
          const row = virtualRows[virtualItem.index];

          if (row?.kind !== 'item') {
            continue;
          }

          const nodeIds = sentinelEndIndexes.get(row.itemIndex);

          if (!nodeIds) {
            continue;
          }

          for (const nodeId of nodeIds) {
            visibleNodeIds.add(nodeId);

            if (fetchedNodeEndIndexesRef.current.get(nodeId) === row.itemIndex) {
              continue;
            }

            fetchedNodeEndIndexesRef.current.set(nodeId, row.itemIndex);
            onFetchNextPageForNode(nodeId);
          }
        }
      }

      for (const nodeId of fetchedNodeEndIndexesRef.current.keys()) {
        if (!visibleNodeIds.has(nodeId)) {
          fetchedNodeEndIndexesRef.current.delete(nodeId);
        }
      }
    }, [
      hasNextPage,
      isFetching,
      isStale,
      lastVisibleRowIndex,
      listPaginationKey,
      onFetchNextPage,
      onFetchNextPageForNode,
      sentinelEndIndexes,
      virtualRows,
      virtualizer,
    ]);

    return (
      <div
        className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent flex min-h-0 flex-1 flex-col overflow-y-auto"
        data-testid="WORKSPACE_AGENT_SELECT_ASSET_LIST_SCROLLER"
        ref={listRef}
        style={WORKSPACE_AGENT_MENTION_LIST_RING_CLEARANCE_STYLE}
      >
        {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: the role is chosen per source, and both listbox and tree name themselves */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: role is listbox or tree; this is the keyboard host when search is hidden */}
        <div
          aria-activedescendant={activeOptionId}
          aria-busy={isLoading}
          aria-disabled={isStale && items.length > 0}
          aria-label={ariaLabel}
          className="relative w-full shrink-0"
          data-testid="WORKSPACE_AGENT_SELECT_ASSET_LIST"
          onKeyDown={onKeyDown}
          role={role}
          style={{ height: virtualizer.getTotalSize() }}
          tabIndex={-1}
        >
          {virtualItems.map((virtualRow) => {
            const row = virtualRows[virtualRow.index];

            if (!row) {
              return null;
            }

            return (
              <WorkspaceAgentSelectAssetVirtualRowView
                getIsBoardItemDisabled={getIsBoardItemDisabled}
                highlightedIndex={clampedHighlightedIndex}
                isKeyboardHighlight={isKeyboardHighlight}
                key={row.id}
                onHighlight={onHighlight}
                onSelectItem={onSelectItem}
                onToggleExpanded={onToggleExpanded}
                pendingChildCountNodeIds={pendingChildCountNodeIds}
                row={row}
                style={{ height: virtualRow.size, transform: `translateY(${virtualRow.start}px)` }}
              />
            );
          })}
        </div>
        {isResolving ? (
          <div className="flex items-center justify-center py-6">
            <Spinner aria-hidden className="size-6 text-gray-9" />
          </div>
        ) : null}
        {items.length === 0 && !isResolving ? (
          <div className="px-3.5 py-2 text-12 text-gray-10" data-testid="WORKSPACE_AGENT_SELECT_ASSET_EMPTY_STATE">
            <p>{emptyStateTitle}</p>
          </div>
        ) : null}
        {boardListQueries}
      </div>
    );
  },
);

WorkspaceAgentSelectAssetVirtualList.displayName = 'WorkspaceAgentSelectAssetVirtualList';
